import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Rocket } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import StatsBar from "@/components/StatsBar";
import PixelGrid from "@/components/PixelGrid";
import ClaimPanel from "@/components/PurchaseModal";
import ShareModal from "@/components/ShareModal";
import Pricing from "@/components/Pricing";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import FeaturedRail from "@/components/FeaturedRail";
import SearchBar from "@/components/SearchBar";
import useSEO, { orgSchema, websiteSchema, productSchema, faqSchema } from "@/lib/useSEO";
import { FAQ_ITEMS } from "@/lib/faqData";

// Module-level motion variants — avoid re-allocating on every render.
const FADE_INIT = { opacity: 0, y: 20 };
const FADE_INIT_LG = { opacity: 0, y: 24 };
const FADE_ANIMATE = { opacity: 1, y: 0 };
const FADE_T0 = { duration: 0.7 };
const FADE_T1 = { duration: 0.8, delay: 0.05 };
const FADE_T2 = { duration: 0.8, delay: 0.15 };
const FADE_T3 = { duration: 0.8, delay: 0.25 };


export default function Landing() {
  const [selection, setSelection] = useState(null);
  const [promo, setPromo] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [shareBlock, setShareBlock] = useState(null);
  const [highlightBlockId, setHighlightBlockId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const gridRef = useRef(null);
  const [searchParams] = useSearchParams();

  const loadPromo = useCallback(() => {
    http.get("/promo/status").then(({ data }) => setPromo(data)).catch(() => {});
  }, []);
  useEffect(() => { loadPromo(); }, [loadPromo]);

  // Auto-pan to a block when landing has ?claimed=<block_id> (post-payment redirect).
  useEffect(() => {
    const claimed = searchParams.get("claimed");
    if (claimed) {
      setHighlightBlockId(claimed);
      setTimeout(() => document.querySelector("#explore")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [searchParams, refreshKey]);

  const onClaimed = (block) => {
    setRefreshKey((k) => k + 1);
    loadPromo();
    if (block) setShareBlock(block);
  };

  // Rich SEO meta + JSON-LD for the homepage. WebSite + Organization + Product
  // + FAQPage all injected so Google/Bing/AI crawlers understand the site.
  useSEO({
    title:
      "PixelEmpire AI — Permanent Digital Advertising Grid · 10,000 Blocks · $0.50/pixel",
    description:
      "Claim a permanent 10×10 advertising block on the PixelEmpire AI 100×100 grid. One-time $0.50/pixel payment. Lifetime placement. Do-follow SEO backlink. Only 10,000 blocks will ever exist — buy early to lock in the lowest rate.",
    path: "/",
    jsonLd: [orgSchema, websiteSchema, productSchema, faqSchema(FAQ_ITEMS)],
  });

  return (
    <div className="min-h-screen relative z-10">
      <Header />
      <PromoBanner refreshTick={refreshKey} />

      {/* GRID FIRST — visitors immediately see the actual companies on the
          board before the marketing hero. Search bar sits right above so
          category filtering feels connected to the canvas. */}
      <section id="explore" className="max-w-6xl mx-auto px-4 pt-28 sm:pt-32">
        <div className="text-center mb-6">
          <div className="pill mx-auto"><span>Live grid · 100 × 100 · 10,000 blocks</span></div>
          <h1
            className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-white"
            data-testid="home-h1"
          >
            Permanent digital advertising on the <span className="text-gradient">pixel empire</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-[var(--fg-muted)] max-w-2xl mx-auto">
            {promo?.is_free_phase
              ? <>First 50 blocks are <strong className="text-emerald-300">FREE</strong> · {promo.remaining} left. After that, $0.50 per pixel — one-time — for lifetime placement + a do-follow SEO backlink.</>
              : <>Claim your permanent spot on the digital grid for <strong className="text-white">$0.50 per pixel</strong> ($50 per 10×10 block). One-time payment. Lifetime placement. Do-follow SEO backlink.</>}
          </p>
        </div>
        <SearchBar onFilterChange={setCategoryFilter} />
      </section>

      {categoryFilter && (
        <div className="max-w-6xl mx-auto px-4 mt-3 flex items-center justify-center" data-testid="category-filter-bar">
          <button
            type="button"
            onClick={() => setCategoryFilter("")}
            className="pill hover:bg-white/10 transition"
            data-testid="clear-category-filter"
          >
            <span>Showing category: <strong className="text-white">{categoryFilter}</strong></span>
            <span className="text-[var(--fg-muted)] ml-2">× clear</span>
          </button>
        </div>
      )}

      <section className="max-w-6xl mx-auto px-4 mt-6">
        <PixelGrid
          ref={gridRef}
          onClusterSelect={setSelection}
          refreshKey={refreshKey}
          highlightBlockId={highlightBlockId}
          categoryFilter={categoryFilter}
        />
        <p className="mt-3 text-center text-xs text-[var(--fg-muted)]">
          Scroll / pinch to zoom · drag on empty area to select a cluster ·{" "}
          <span className="sm:hidden">tap a claimed block to preview it — tap again to visit</span>
          <span className="hidden sm:inline">hover to preview · click a claimed block to visit</span>
        </p>
      </section>

      <div className="relative z-10 mt-14">
        <StatsBar />
      </div>

      {/* ── SEO-rich explainer copy — What / How / Why. Uses H2/H3 hierarchy
          under the single homepage H1, and reinforces the keyword themes the
          AI crawlers grade the page against. ──────────────────────────── */}
      <section
        id="what-is-pixel-empires"
        className="max-w-4xl mx-auto px-4 mt-20"
        data-testid="explainer-section"
      >
        <div className="pill" data-testid="explainer-pill"><span>Explainer</span></div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-tight text-white">
          What is PixelEmpire AI?
        </h2>
        <p className="mt-4 text-base text-[var(--fg-muted)] leading-relaxed">
          PixelEmpire AI is a permanent digital advertising grid inspired by Alex Tew's iconic
          2005 Million Dollar Homepage. Businesses claim a 10×10 block on our 100×100 canvas,
          upload their logo, and their ad stays live on <strong className="text-white">pixel-empires.com</strong> for as long as
          the site exists — no renewals, no monthly fees, no expiration. Every block also earns
          a real do-follow SEO backlink from our public <a href="/directory" className="text-cyan-300 underline">business directory</a>.
        </p>

        <h3 className="mt-10 font-display text-xl text-white">How it works</h3>
        <ol className="mt-3 space-y-2 text-[var(--fg-muted)] list-decimal list-inside">
          <li>Open the grid above and drag-select any empty area (minimum 10×10 pixels).</li>
          <li>Upload your logo, set your destination URL, write a 50-word description, pick a category.</li>
          <li>Pay $0.50 per pixel via Stripe (or claim free while promo blocks last).</li>
          <li>Your ad publishes within seconds — and stays live forever.</li>
        </ol>
        <p className="mt-3 text-sm text-[var(--fg-muted)]">
          Full walkthrough: <a href="/how-it-works" className="text-cyan-300 underline">/how-it-works</a>
        </p>

        <h3 className="mt-10 font-display text-xl text-white">Why only 10,000 blocks exist</h3>
        <p className="mt-3 text-[var(--fg-muted)] leading-relaxed">
          Scarcity is the point. The Million Dollar Homepage worked because there was exactly one
          million pixels and no way to add more. PixelEmpire follows the same rule: 100×100 grid,
          10,000 blocks, and once they're claimed, no new inventory will ever be added. The earlier
          you claim, the better your position — and the lower your price locks in.
        </p>

        <h3 className="mt-10 font-display text-xl text-white">Why buying early is valuable</h3>
        <ul className="mt-3 space-y-2 text-[var(--fg-muted)] list-disc list-inside">
          <li><strong className="text-white">Best real estate.</strong> Center blocks and clean rectangles sell first.</li>
          <li><strong className="text-white">Price lock.</strong> Today's rate is the floor. Prices only ever increase.</li>
          <li><strong className="text-white">Compounding.</strong> A block claimed on day 1 earns clicks for the entire lifetime of the site.</li>
          <li><strong className="text-white">Directory ordering.</strong> Early advertisers appear first on our public /directory.</li>
        </ul>

        <h3 className="mt-10 font-display text-xl text-white">Why your ad stays permanent</h3>
        <p className="mt-3 text-[var(--fg-muted)] leading-relaxed">
          Traditional ad networks charge monthly or per-click because their revenue depends on
          you keeping the tap open. PixelEmpire is different: we take a single one-time payment
          and your block is yours for the life of the platform. No auto-renew. No sneaky
          charges. You can even edit the logo, URL, description and category anytime from your
          dashboard — the block itself simply stays.
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#explore" className="btn-primary" data-testid="explainer-cta-grid">
            Claim your block <ArrowRight className="w-4 h-4" />
          </a>
          <a href="/how-it-works" className="btn-ghost">Read the full guide</a>
        </div>
      </section>

      {/* Marketing hero moved BELOW the grid so it becomes secondary content —
          visitors have already seen the actual product. */}
      <section className="relative pt-16 pb-8 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={FADE_INIT} animate={FADE_ANIMATE} transition={FADE_T0}
            className="pill mx-auto"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>10,000 units · unlimited possibilities</span>
          </motion.div>

          <motion.h2
            initial={FADE_INIT_LG} animate={FADE_ANIMATE} transition={FADE_T1}
            className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white"
          >
            The neon<br />
            <span className="text-gradient">pixel empire.</span>
          </motion.h2>

          <motion.p
            initial={FADE_INIT_LG} animate={FADE_ANIMATE} transition={FADE_T2}
            className="mt-6 text-lg text-[var(--fg-muted)] max-w-2xl mx-auto"
          >
            Claim your permanent spot on the digital grid for <strong className="text-white">$0.50 per pixel</strong> ($50 per 10x10 block).
            Drive traffic to your startup <em className="text-white">forever</em> with a premium, permanent SEO backlink.
          </motion.p>

          <motion.div
            initial={FADE_INIT} animate={FADE_ANIMATE} transition={FADE_T2}
            className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-sm text-emerald-300 font-medium"
            data-testid="hero-lifetime-badge"
          >
            <ShieldCheck className="w-4 h-4" /> Owned for LIFETIME · One-time payment · No renewals
          </motion.div>

          <motion.div
            initial={FADE_INIT_LG} animate={FADE_ANIMATE} transition={FADE_T3}
            className="mt-8 flex items-center justify-center gap-3 flex-wrap"
          >
            <a href="#explore" data-testid="cta-explore" className="btn-primary">
              {promo?.is_free_phase
                ? <><Rocket className="w-4 h-4" /> Claim FREE block · {promo.remaining} left</>
                : <>Explore the grid <ArrowRight className="w-4 h-4" /></>}
            </a>
            <a href="#pricing" data-testid="cta-pricing" className="btn-ghost">See pricing</a>
          </motion.div>

          <div className="mt-14 grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {[
              { icon: Zap, label: "Auto-published", copy: "Ads go live the moment payment clears" },
              { icon: ShieldCheck, label: "Stripe-secure", copy: "Cards, wallets, Apple / Google Pay" },
              { icon: Sparkles, label: promo?.is_free_phase ? "Free launch" : "Flat pricing",
                copy: promo?.is_free_phase ? "First 50 blocks · $0 · no card" : "$0.50 per pixel · $50 per block · lifetime" },
            ].map(({ icon: Icon, label, copy }, i) => (
              <div key={label} className="glass p-4 text-left animate-fade-up" style={{ animationDelay: `${400 + i * 80}ms` }}>
                <Icon className="w-4 h-4 text-neon-cyan" />
                <div className="mt-2 font-semibold">{label}</div>
                <div className="text-xs text-[var(--fg-muted)]">{copy}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-20"><FeaturedRail /></div>
      <Pricing promo={promo} />
      <FAQ />
      <Contact />
      <Footer />

      <ClaimPanel
        open={!!selection}
        selection={selection}
        promo={promo}
        onClose={() => setSelection(null)}
        onReset={() => {
          setSelection(null);
          // Broadcast a reset event so PixelGrid can wipe any residual state.
          window.dispatchEvent(new CustomEvent("pixel:selection-reset"));
        }}
        onClaimed={onClaimed}
      />

      <ShareModal
        open={!!shareBlock}
        block={shareBlock}
        onClose={() => setShareBlock(null)}
      />
    </div>
  );
}
